<div class="carbon-footprint-container">
  <header class="carbon-footprint-heading">
    <h1>{{ 'sustainability.carbon-footprint' | translate }}</h1>
  </header>
  <div class="information-banner">
    <agr-extended-notification
      notificationType="info"
      [title]="'sustainability.notification-title' | translate"
      [actionText]="getActionText()"
      (action)="toggleNotification()"
    >
      <div>
        <div *ngIf="showNotification">
          <div
            class="text--small"
            [innerHTML]="'sustainability.notification-description' | translate"
          ></div>
        </div>
      </div>
    </agr-extended-notification>
  </div>

  <div class="footprint-content" *ngIf="(flocks$ | async)?.length > 0">
    <div class="carbon-footprint-workspace">
      <section class="calculation-setup surface" aria-labelledby="calculation-setup-title">
        <h2 id="calculation-setup-title">
          {{ 'sustainability.workflow.new-calculation' | translate }}
        </h2>
        <mat-form-field class="item-selector">
          <mat-label>{{ 'flock-overview.placeholder' | translate }}</mat-label>
          <mat-select
            [hideSingleSelectionIndicator]="true"
            [value]="selectedFlock"
            placeholder="{{ 'flock-overview.placeholder' | translate }}"
            data-test="flock-overview__flock-selector1"
            (selectionChange)="onFlockSelected($event.value)"
          >
            <mat-option *ngFor="let flock of flocks$ | async" [value]="flock">
              <b>{{ flock.name }}</b> - {{ 'flock-overview.selector-option-barn' | translate }}
              {{ flock.arrival?.barn?.name }} -
              {{ flock.arrival?.arrivalDate | agrDate: 'shortDate' }}
            </mat-option>
          </mat-select>
        </mat-form-field>
        <p class="flock-period" *ngIf="selectedFlock">
          {{ selectedFlock.arrival?.arrivalDate | date: 'shortDate' }}
          <ng-container *ngIf="selectedFlock.departure?.departureDate">
            – {{ selectedFlock.departure.departureDate | date: 'shortDate' }}
          </ng-container>
        </p>
        <ng-container *ngIf="selectedFlock">
          <section
            class="calculation-section readiness"
            aria-labelledby="calculation-inputs-title"
            aria-live="polite"
          >
            <header class="section-header">
              <h3 id="calculation-inputs-title">
                {{ 'sustainability.workflow.requirements-title' | translate }}
              </h3>
            </header>
            <p *ngIf="checkingReadiness()" role="status">
              {{ 'sustainability.workflow.checking' | translate }}
            </p>
            <agr-extended-notification
              *ngIf="readinessError()"
              notificationType="error"
              role="alert"
              [title]="'sustainability.workflow.readiness-error' | translate"
              [actionText]="'sustainability.workflow.retry' | translate"
              (action)="checkReadiness()"
            >
            </agr-extended-notification>

            <p *ngIf="readiness()?.canCalculate">
              {{ 'sustainability.workflow.data-complete' | translate }}
            </p>
            <div class="readiness-progress" *ngIf="readiness()">
              <p id="readiness-progress-label">
                {{ completedRequirements() }} / {{ readinessChecks().length }}
                {{ 'sustainability.workflow.requirements-met' | translate }}
              </p>
              <progress
                [value]="completedRequirements()"
                [max]="readinessChecks().length || 1"
                aria-labelledby="readiness-progress-label"
                data-test="carbon-footprint-readiness-progress"
              ></progress>
            </div>
            <ng-container *ngFor="let check of readinessChecks()">
              <agr-extended-notification
                *ngIf="!check.complete"
                notificationType="warning"
                [title]="
                  ('sustainability.workflow.requirements.' + check.requirement | translate) +
                  ' — ' +
                  ('sustainability.workflow.incomplete' | translate)
                "
              >
                <div class="issue-group" *ngFor="let group of check.issueGroups">
                  <p>{{ issueText(group.issues[0]) }}</p>
                  <details
                    *ngIf="group.details.length"
                    class="issue-details"
                    [open]="group.details.length === 1 || check.requirement !== 'feeds'"
                  >
                    <summary>
                      {{ 'sustainability.workflow.issue-details' | translate }} ({{
                        group.details.length
                      }})
                    </summary>
                    <ul class="source-issues">
                      <li *ngFor="let issue of group.details">
                        <strong *ngIf="issue.deliveryLineId"
                          >{{ issue.productTitle }} (#{{ issue.deliveryLineId }})</strong
                        >
                        <strong *ngIf="issue.year">{{ issue.year }}</strong>
                        <span *ngIf="issue.fields?.length" class="source-fields">
                          {{ 'sustainability.workflow.missing-fields' | translate }}:
                          {{ issueFields(issue) }}
                        </span>
                      </li>
                    </ul>
                  </details>
                </div>
                <div class="correction-actions">
                  <a
                    mat-stroked-button
                    *ngIf="check.requirement === 'flock' || check.requirement === 'bedding'"
                    routerLink="/flock"
                  >
                    {{ 'sustainability.workflow.correct-flock' | translate }}
                  </a>
                  <a
                    mat-stroked-button
                    *ngIf="check.requirement === 'feeds'"
                    [routerLink]="['/flock/feed-balance', selectedFlock.id]"
                  >
                    {{ 'feed-balance.title' | translate }}
                  </a>
                  <a
                    mat-stroked-button
                    *ngIf="check.requirement === 'farm-resources'"
                    routerLink="/farm-resources"
                  >
                    {{ 'sustainability.workflow.correct-resources' | translate }}
                  </a>
                </div>
              </agr-extended-notification>
            </ng-container>
            <ul class="ready-checks">
              <ng-container *ngFor="let check of readinessChecks()">
                <li *ngIf="check.complete">
                  <span>{{
                    'sustainability.workflow.requirements.' + check.requirement | translate
                  }}</span>
                  <span class="ready-label">{{ 'sustainability.workflow.ready' | translate }}</span>
                </li>
              </ng-container>
            </ul>
            <button
              mat-stroked-button
              (click)="checkReadiness()"
              [disabled]="checkingReadiness() || calculating()"
            >
              {{ 'sustainability.workflow.check-again' | translate }}
            </button>
            <p *ngIf="calculating()" role="status">
              {{ 'sustainability.workflow.submitting' | translate }}
            </p>
            <agr-extended-notification
              *ngIf="calculationError()"
              notificationType="error"
              role="alert"
              [title]="'sustainability.workflow.issues.' + calculationError() | translate"
            >
            </agr-extended-notification>
          </section>
        </ng-container>
        <div class="calculation-actions">
          <button
            mat-flat-button
            color="primary"
            [disabled]="!canCalculate()"
            (click)="startCalculateCarbonFootprint()"
            data-test="flock-overview__start-calculate-carbon-footprint-button"
          >
            {{ 'sustainability.start-calculation' | translate }}
          </button>
        </div>
      </section>
      <div class="result-column" *ngIf="selectedFlock">
        <section
          class="calculation-section surface calculation-results"
          [class.result-panel--populated]="selectedCalculation()"
          aria-labelledby="calculation-results-title"
          aria-live="polite"
        >
          <header class="section-header">
            <h2 id="calculation-results-title">
              {{ 'sustainability.workflow.saved-result' | translate }}
            </h2>
            <button
              mat-flat-button
              color="primary"
              [disabled]="calculating() || loadingResults()"
              (click)="refreshCarbonFootprintResult()"
              data-test="flock-overview__refresh-carbon-footprint-result-button"
            >
              <mat-icon aria-hidden="true">refresh</mat-icon>
              {{ 'sustainability.refresh-carbon-footprint' | translate }}
            </button>
          </header>
          <p *ngIf="loadingResults()" role="status">
            {{ 'sustainability.workflow.loading-results' | translate }}
          </p>
          <agr-extended-notification
            *ngIf="resultsError()"
            notificationType="error"
            role="alert"
            [title]="'sustainability.workflow.results-error' | translate"
            [actionText]="'sustainability.workflow.retry' | translate"
            (action)="refreshCarbonFootprintResult()"
          >
          </agr-extended-notification>
          <agr-extended-notification
            *ngIf="status() === 'pending' || status() === 'inprogress'"
            notificationType="info"
            [title]="'sustainability.workflow.pending' | translate"
            [actionText]="'sustainability.workflow.check-results' | translate"
            (action)="refreshCarbonFootprintResult()"
          >
          </agr-extended-notification>
          <agr-extended-notification
            *ngIf="status() === 'failed' || status() === 'timedout'"
            notificationType="warning"
            [title]="'sustainability.workflow.failed' | translate"
          >
          </agr-extended-notification>
          <agr-extended-notification
            *ngIf="
              status() === 'missing' &&
              !loadingResults() &&
              !resultsError() &&
              !calculations().length
            "
            notificationType="info"
            [title]="'sustainability.workflow.no-results' | translate"
          >
          </agr-extended-notification>
          <div *ngIf="selectedCalculation() as calculation" class="result-context">
            <strong>{{ selectedFlock.name }}</strong>
            <div>
              <span
                >{{ 'sustainability.workflow.calculation' | translate }} #{{ calculation.id }}</span
              >
              <span
                class="result-status"
                [class.result-status--failed]="calculationStatus(calculation) === 'failed'"
              >
                {{
                  'sustainability.workflow.statuses.' + calculationStatus(calculation) | translate
                }}
              </span>
            </div>
            <time [attr.datetime]="calculation.calculatedAt || calculation.createdAt">
              {{ calculation.calculatedAt || calculation.createdAt | date: 'short' }}
            </time>
          </div>
          <div class="footprint-totals" *ngIf="selectedCalculation() as calculation">
            <div class="footprint-total" *ngFor="let impact of climateImpacts(calculation)">
              <span>{{ 'sustainability.workflow.climate-change' | translate }}</span>
              <strong>{{ impact.value | number: '1.3-3' }}</strong>
              <span>{{
                (impact.animalType === 'Consumption egg'
                  ? 'sustainability.workflow.outputs.eggs'
                  : 'sustainability.workflow.outputs.hens'
                ) | translate
              }}</span>
              <span class="impact-unit">{{
                (impact.animalType === 'Consumption egg'
                  ? 'sustainability.workflow.outputs.egg-unit'
                  : 'sustainability.workflow.outputs.hen-unit'
                ) | translate
              }}</span>
            </div>
          </div>
          <agr-table
            *ngIf="
              selectedCalculation() &&
              carbonFootprintErrors.length === 0 &&
              tableConfig.dataSource.data.length > 0
            "
            [config]="tableConfig"
            [emptyTitle]="''"
            [emptyDescription]="''"
          >
          </agr-table>
          <agr-extended-notification
            *ngIf="
              selectedCalculation() &&
              carbonFootprintErrors.length === 0 &&
              tableConfig.dataSource.data.length === 0 &&
              climateImpacts(selectedCalculation()).length === 0
            "
            notificationType="info"
            [title]="'sustainability.workflow.no-impact-values' | translate"
          >
          </agr-extended-notification>
          <agr-extended-notification
            *ngIf="carbonFootprintErrors.length > 0"
            notificationType="error"
            role="alert"
            [title]="'sustainability.workflow.result-errors' | translate"
          >
            <ul class="source-issues">
              <li *ngFor="let error of carbonFootprintErrors">{{ error }}</li>
            </ul>
          </agr-extended-notification>
        </section>
        <section
          *ngIf="selectedFlock"
          class="surface history-panel"
          aria-labelledby="calculation-history-title"
        >
          <h2 id="calculation-history-title">
            {{ 'sustainability.workflow.previous-calculations' | translate }}
          </h2>
          <p>{{ 'sustainability.workflow.history-description' | translate }}</p>
          <div class="history-cards">
            <button
              *ngFor="let calculation of calculations()"
              type="button"
              class="history-card"
              [class.history-card--selected]="selectedCalculationId() === calculation.id"
              [attr.aria-pressed]="selectedCalculationId() === calculation.id"
              (click)="selectCalculation(calculation.id)"
              data-test="carbon-footprint-history-item"
            >
              <strong
                >{{ 'sustainability.workflow.calculation' | translate }} #{{
                  calculation.id
                }}</strong
              >
              <time [attr.datetime]="calculation.calculatedAt || calculation.createdAt">
                {{ calculation.calculatedAt || calculation.createdAt | date: 'short' }}
              </time>
              <span
                class="result-status"
                [class.result-status--failed]="calculationStatus(calculation) === 'failed'"
              >
                {{
                  'sustainability.workflow.statuses.' + calculationStatus(calculation) | translate
                }}
              </span>
              <span *ngFor="let impact of climateImpacts(calculation)" class="history-impact">
                <b>{{ impact.value | number: '1.3-3' }}</b>
                <span>{{
                  (impact.animalType === 'Consumption egg'
                    ? 'sustainability.workflow.outputs.eggs'
                    : 'sustainability.workflow.outputs.hens'
                  ) | translate
                }}</span>
                <span>{{
                  (impact.animalType === 'Consumption egg'
                    ? 'sustainability.workflow.outputs.egg-unit'
                    : 'sustainability.workflow.outputs.hen-unit'
                  ) | translate
                }}</span>
              </span>
            </button>
          </div>
          <p *ngIf="!calculations().length">
            {{ 'sustainability.workflow.no-results' | translate }}
          </p>
        </section>
      </div>
    </div>
    <div class="sustell-logo-container">
      <span>Powered by</span>
      <img
        *ngIf="sustellLogoSrc !== null"
        class="sustell-logo"
        [src]="sustellLogoSrc"
        alt="Sustell"
      />
    </div>
  </div>
</div>
